import './driverTips.scss';

import BottomDrawer from '@transferz/fe-component-library/src/components/bottomDrawer/BottomDrawer';
import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import Input from '@transferz/fe-component-library/src/components/input/Input';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { IJourney } from '@transferz/fe-models';
import { useLocalize } from 'localize-react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { useMutationCreateGratuity } from '../../../../queries/booking/hooks/useMutationCreateGratuity';

export interface ITransferCompanyDetailsProps {
  journey: IJourney;
  eligibility: any;
  bookingPinCode: string;
}

export const DriverTips: React.FC<ITransferCompanyDetailsProps> = ({ journey, eligibility: { gratuityRecomendations }, bookingPinCode }) => {
  const { translate, locale } = useLocalize();
  let buttonVariables = gratuityRecomendations?.map((item: any) => item.amount);
  const [tipAmount, setTipAmount] = useState<any>(buttonVariables?.[1]);
  const [customTipAmount, setCustomTipAmount] = useState<any>(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [inputSize, setInputSize] = useState(1);
  const inputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const paymentSuccessfull = searchParams.get('paymentSuccessfull');
  const tipedAmount = searchParams.get('tipedAmount');
  const [createGratuityForJourney] = useMutationCreateGratuity();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (buttonVariables && !tipAmount) {
      buttonVariables = buttonVariables.sort((a: number, b: number) => a - b);
      setTipAmount(buttonVariables[0]);
    }
  }, [buttonVariables]);

  async function handleAddTipPayment() {
    try {
      setLoading(true);
      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set('paymentSuccessfull', 'true');
      successUrl.searchParams.set('tipedAmount', tipAmount);
      const { data } = await createGratuityForJourney({
        variables: {
          params: {
            bookingPinCode: bookingPinCode,
            journeyId: journey.id,
            amount: Number(tipAmount),
            currencyCode: 'EUR',
          },
          optionalPaymentLinkParams: {
            locale: locale,
            returnUrl: successUrl.toString(),
          },
        },
      });

      if (data?.createGratuityForJourney?.paymentLink?.url) {
        // eslint-disable-next-line require-atomic-updates
        window.location.href = data?.createGratuityForJourney?.paymentLink?.url;
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card-wrap">
      <Card
        title={translate('tip_your_driver')}
        collapsed={false}
        collapsibleIcon={false}
        isGrey={false}
      >
        {
          paymentSuccessfull ? <div className="success-tip">{translate('success_tip', { currencyCode: '€', tipAmount: tipedAmount ?? tipAmount, driverName: journey?.driver?.name || 'your driver' })}</div>
            : <>
              <Typography variant="body1" className="mb-16">{translate('tip_advice')}</Typography>
              <div className="d-flex mb-16">
                <Button
            priority="secondary"
            label={`€${(Math.round(buttonVariables[0] * 100) / 100).toFixed(2)}`}
            onClick={() => {
              setTipAmount(buttonVariables[0]);
              setCustomTipAmount(0);
            }}
            fullWidth={true}
            className={'left-button' + ' ' + ((tipAmount === buttonVariables[0]) ? 'selected' : '')}
                />
                <Button
            priority="secondary"
            label={`€${(Math.round(buttonVariables[1] * 100) / 100).toFixed(2)}`}
            onClick={() => {
              setTipAmount(buttonVariables[1]);
              setCustomTipAmount(0);
            }}
            fullWidth={true}
            className={'middle-button' + ' ' + ((tipAmount === buttonVariables[1]) ? 'selected' : '')}
                />
                <Button
            priority="secondary"
            label={`€${(Math.round(buttonVariables[2] * 100) / 100).toFixed(2)}`}
            onClick={() => {
              setTipAmount(buttonVariables[2]);
              setCustomTipAmount(0);
            }}
            fullWidth={true}
            className={'right-button' + ' ' + ((tipAmount === buttonVariables[2]) ? 'selected' : '')}
                />
              </div>
              <div className="d-flex mb-16">
                <Button
            priority="secondary"
            label={!buttonVariables.includes(tipAmount)
              ? <div>
                <div className="custom-amount">{(`€${(Math.round(tipAmount * 100) / 100).toFixed(2)}`)}</div>
                <div>Edit</div>
              </div>
              : translate('enterCustomAmount')}
            fullWidth={true}
            onClick={() => {
              setIsPopupOpen(true);
              //@ts-ignore
              inputRef?.current?.focus();
            }}
            className={customTipAmount ? 'selected' : ''}
                />
              </div>
              <div className="d-flex mb-16">
                <Button
                priority="primary"
                variant="info"
                label={translate('add_tip')}
                fullWidth={true}
                onClick={handleAddTipPayment}
                loading={loading}
                />
              </div>
            </>}
        <Typography color="asphalt5" variant="body1">{translate('tip_advice2')}</Typography>
      </Card>
      <BottomDrawer
        title={translate('enterCustomAmount')}
        isOpen={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setCustomTipAmount(0);
        }}
        enableDesktopView={true}
      >
        <div className={'input-wrapper' + ' ' + (!customTipAmount ? 'zero' : '')}>
          €
          <Input
            size={inputSize}
            ref={inputRef}
            inputMode="numeric"
            color={'asphalt5'}
            value={customTipAmount}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              event.persist();
              if (isNaN(Number(event.target.value))) {
                return;
              }
              setInputSize(() => event.target?.value?.toString()?.length < 1
                ? 1
                : event.target?.value?.toString()?.length,
              );
              setCustomTipAmount(event.target.value.replace(/^0+/, ''));
            }
            } />
        </div>
        <div className="d-flex mb-16">
          <Button
            priority="primary"
            variant="info"
            label={translate('save')}
            fullWidth={true}
            onClick={() => {
              setTipAmount(customTipAmount);
              setIsPopupOpen(false);
            }
            }
          />
        </div>
        <Typography color="asphalt5" variant="body1">{translate('tip_advice2')}</Typography>
      </BottomDrawer>
    </div>
  );
};
